import React, { useState } from 'react';
import classNames from 'classnames';
import { useViewEvent } from '@stitch-fix/event-reporter';
import StickerLabel from '../StickerLabel';
import * as LaydownHelpers from './LaydownHelpers';
import { ImageComponent, OutfitItemLabel } from './types';
import styles from './outfitImageItem.module.scss';

interface Props {
  /**
   * How many times the dots on the outfit items are reanimated. The dots
   * animate on first render, but we allow the animation to be replayed
   * to signal that each item is clickable.
   *
   */
  dotRerenderCount: number;

  /**
   * The data required to render an outfit item.
   */
  imageComponent: ImageComponent;

  /**
   * Callback to execute when an item in the
   * outfit is clicked. Helpful for app-specific
   * analytics.
   */
  onOutfitItemClick?: (arg0: ImageComponent) => void;
}

const cssStyle = (imageComponent: ImageComponent) => {
  return {
    height: LaydownHelpers.toPercent(imageComponent.laydown.maxHeight),
    width: LaydownHelpers.toPercent(imageComponent.laydown.maxWidth),
    left: LaydownHelpers.toPercent(imageComponent.laydown.x),
    top: LaydownHelpers.topCss(imageComponent.laydown),
    transform: LaydownHelpers.transformCss(imageComponent.laydown),
    justifyContent: LaydownHelpers.justifyContentCss(imageComponent.laydown),
  };
};

const Label = ({ label }: { label: OutfitItemLabel }) => {
  const { text, theme } = label;

  return (
    <div className={classNames(styles.label)}>
      <StickerLabel variant={theme}>{text}</StickerLabel>
    </div>
  );
};

const OutfitImageItem = ({
  imageComponent,
  onOutfitItemClick,
  dotRerenderCount,
}: Props) => {
  const { ref, EventProvider, reportSelectEvent } = useViewEvent({
    schema: 'sku_view',
    name: 'outfit_sku',
    sku_id: imageComponent.id,
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const handleOutfitItemClick = () => {
    setIsAnimating(true);

    reportSelectEvent({
      schema: 'sku_select',
      name: 'outfit_sku',
      sku_id: imageComponent.id,
      action_name: 'navigate_to_pdp',
    });

    if (onOutfitItemClick) {
      onOutfitItemClick(imageComponent);
    }
  };

  const labelData =
    imageComponent.label ||
    (imageComponent.labelText ? { text: imageComponent.labelText } : null);
  const label = labelData && <Label label={labelData} />;

  return (
    <EventProvider>
      <a
        href={imageComponent.url}
        onClick={handleOutfitItemClick}
        data-testid="outfit-image-container"
        ref={ref}
        className={classNames(styles['outfit-image-container'], {
          [styles['anchor-item']]: labelData,
        })}
        style={cssStyle(imageComponent)}
      >
        <div className={styles['outfit-image-wrapper']}>
          <img
            data-testid={isAnimating ? 'is-animating' : ''}
            className={classNames(styles['outfit-image'], {
              [styles['is-animating']]: isAnimating,
            })}
            src={imageComponent.imageUrl}
            alt={imageComponent.altText}
          />

          {label}
        </div>

        <div
          key={dotRerenderCount}
          data-testid="animating-dot"
          className={styles['animating-dot']}
        />
      </a>
    </EventProvider>
  );
};

export default OutfitImageItem;
